// ActivityDrawer — the shared right-slide detail panel for the
// schedule view (spec §10.2/§10.3). Composes the EXISTING
// SlideOverDrawer via its opt-in `noBackdrop` (spec §10.1: no scrim,
// non-modal, page stays interactive) at the 420px `activity` width.
// Structure: head (prev/next + close, provided by SlideOverDrawer's
// header for close), kind eyebrow (type-coloured ≤8px dot + uppercase
// kind — R1 hairline), title, contract context, status pill, the
// per-kind DETAILS table (§10.3 field labels), optional SERIES
// (SeriesProgressStrip, only when seriesNeighbours present), ABOUT
// prose, LINKED CLAUSES, ACTIVITY log, and the action bar containing
// only "Open in contract →". Edit-in-drawer is a future scope item;
// users edit by following Open in contract → into the per-tab editor.
// "Open in contract →" navigates via the EXISTING ManagedContractDetail
// convention `?tab=<slug>&item=<id>` (verified against the route — query
// param, not path segment; the per-row param is `item` matching the bare
// entity id = sourceId; a variation maps to the combined `variations-sows`
// tab). Prev/Next walk the currently-filtered set in order, wrapping at
// boundaries.
//
// Currency values (milestone value, insurance sum insured, variation
// value impact) render through formatCurrencyWithGst so the mandatory
// ex-GST / no-GST / "not recorded" suffix is always visible — unlabelled
// currency on contract surfaces is contractually unsafe. Activity-log
// timestamps render through formatActivityLogTimestamp so the raw
// occurredAt ISO string never reaches the user.

import SlideOverDrawer from "../../common/SlideOverDrawer";
import SeriesProgressStrip from "./SeriesProgressStrip";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { gstSuffix } from "../../../utils/gstSuffix";
import { formatAud2dp } from "../../../utils/formatCurrency";
import type {
  Activity,
  ActivityDetail,
  ActivityKind,
  ActivityDetailSection,
  OccurrenceStatus,
} from "../../../types/schedule";

interface ActivityDrawerProps {
  activityId: string;
  // The currently-filtered, ordered activity set the prev/next walk.
  activityList: Activity[];
  detail: ActivityDetail | undefined;
  isLoading: boolean;
  onClose: () => void;
  onNavigate: (activityId: string) => void;
}

// kind → the REAL ManagedContractDetail tab slug (verified against
// TAB_SLUGS in ManagedContractDetail.tsx). There is no standalone
// `variation` tab — it lives under the combined `variations-sows`
// tab. The deep-link param is `item` and matches the bare entity id
// (= Activity.sourceId), NOT the composite Activity.id.
const KIND_TO_TAB_SLUG: Record<ActivityKind, string> = {
  insurance: "insurance",
  deliverable: "deliverable",
  milestone: "milestone",
  meeting: "meeting",
  kpi: "kpi",
  variation: "variations-sows",
};

const KIND_DOT: Record<ActivityKind, string> = {
  insurance: "bg-amber-500",
  deliverable: "bg-sky-500",
  milestone: "bg-emerald-500",
  meeting: "bg-brand-500",
  kpi: "bg-teal-500",
  variation: "bg-fuchsia-500",
};

// null/undefined → the app-wide "—" em-dash convention. A KPI with no
// measurement children carries an explicit null currentReading.
function val(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

// Full-precision AUD + the GST suffix sourced from the contract's stored
// intent. Mirrors the milestone-strip / contract-detail convention so
// currency reads consistently across the app.
function formatCurrencyWithGst(
  value: number | null | undefined,
  gstApplies: boolean | undefined,
): string {
  if (value === null || value === undefined) return "—";
  return `${formatAud2dp(value)} (${gstSuffix(gstApplies)})`;
}

// Renders the stored ISO occurredAt as a Brisbane-localised date+time
// ("15 May 2026, 10:10 am") — the raw ISO string is too noisy for the
// drawer's audit-log column. Falls back to the raw value on parse failure
// so we never strip information silently.
function formatActivityLogTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Australia/Brisbane",
  });
}

// Renders an ISO YYYY-MM-DD date as "1 May 2026". Falls back to the raw
// string when parsing fails so absent / malformed values still surface.
function formatDateValue(v: string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const d = new Date(`${v}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

// Per-kind §10.3 field rows. Labels are the spec table's exact
// wording. Optional fields render "—" rather than disappearing so the
// table shape is stable across rows. Currency cells receive the contract
// GST suffix so the dollar value is never unlabelled; date cells render
// via formatDateValue so a raw YYYY-MM-DD never reaches the user.
// `Meeting type` (not "Location") matches the underlying tracking field
// — meetings carry a meetingType string (e.g. "Steering committee"), not
// a venue.
function detailRows(
  section: ActivityDetailSection,
  gstApplies: boolean | undefined,
): { label: string; value: string }[] {
  switch (section.kind) {
    case "insurance":
      return [
        { label: "Expires", value: formatDateValue(section.expires) },
        { label: "Provider", value: val(section.provider) },
        { label: "Policy", value: val(section.policyNumber) },
        {
          label: "Sum insured",
          value: formatCurrencyWithGst(section.sumInsured, gstApplies),
        },
      ];
    case "deliverable":
      return [
        { label: "Due", value: formatDateValue(section.due) },
        { label: "Owner", value: val(section.owner) },
        { label: "Frequency", value: val(section.frequency) },
        { label: "Submission", value: val(section.submission) },
      ];
    case "milestone":
      return [
        { label: "Due", value: formatDateValue(section.due) },
        { label: "Accepted", value: formatDateValue(section.acceptedAt) },
        { label: "Paid", value: formatDateValue(section.paidAt) },
        {
          label: "Value",
          value: formatCurrencyWithGst(section.value, gstApplies),
        },
      ];
    case "meeting":
      return [
        { label: "Scheduled", value: formatDateValue(section.scheduled) },
        { label: "Cadence", value: val(section.cadence) },
        {
          label: "Attendees",
          value: section.attendees?.length
            ? section.attendees.join(", ")
            : "—",
        },
        { label: "Meeting type", value: val(section.location) },
      ];
    case "kpi":
      return [
        { label: "Period close", value: formatDateValue(section.periodClose) },
        { label: "Target", value: val(section.target) },
        { label: "Current reading", value: val(section.currentReading) },
        {
          label: "Compliance window",
          value: val(section.complianceWindow),
        },
      ];
    case "variation":
      return [
        { label: "Effective", value: formatDateValue(section.effective) },
        {
          label: "Value impact",
          value: formatCurrencyWithGst(section.valueImpact, gstApplies),
        },
        { label: "Varies", value: val(section.varies) },
      ];
  }
}

// Map the backend per-occurrence status to the SeriesProgressStrip dot
// vocabulary (1:1 — both use the milestone progress-dot states).
function toDotState(s: OccurrenceStatus): OccurrenceStatus {
  return s;
}

export default function ActivityDrawer({
  activityId,
  activityList,
  detail,
  isLoading,
  onClose,
  onNavigate,
}: ActivityDrawerProps) {
  const currentIndex = activityList.findIndex((a) => a.id === activityId);

  // Prev/Next wrap at the boundaries (spec §10.5): next from the last
  // → first; prev from the first → last.
  const navigateBy = (delta: number): void => {
    if (activityList.length === 0 || currentIndex < 0) return;
    const nextIndex =
      (currentIndex + delta + activityList.length) % activityList.length;
    onNavigate(activityList[nextIndex].id);
  };

  const activity = detail?.activity;

  const header = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label="Previous activity"
        onClick={() => navigateBy(-1)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Next activity"
        onClick={() => navigateBy(1)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );

  const title = activity?.name ?? "Activity";

  return (
    <SlideOverDrawer
      isOpen
      noBackdrop
      width="activity"
      onClose={onClose}
      title={title}
      actions={header}
    >
      {isLoading || !activity || !detail ? (
        <div
          data-testid="activity-drawer-loading"
          className="flex justify-center py-16 text-caption text-slate-500 dark:text-slate-400"
        >
          Loading activity…
        </div>
      ) : (
        <div className="space-y-6">
          {/* Kind eyebrow — type-coloured ≤8px dot + uppercase kind. */}
          <div>
            <div className="flex items-center gap-1.5 text-overline uppercase text-slate-500 dark:text-slate-400">
              <span
                aria-hidden="true"
                className={`h-2 w-2 rounded-full ${KIND_DOT[activity.kind]}`}
              />
              {activity.kind.toUpperCase()}
            </div>
            <p className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
              {activity.contract.name}{" "}
              <span className="font-mono">· {activity.contract.ref}</span>
            </p>
            <span className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-caption font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              {activity.statusLabel}
            </span>
          </div>

          {/* DETAILS — per-kind §10.3 field table. */}
          <section>
            <h3 className="mb-2 text-overline uppercase text-slate-500 dark:text-slate-400">
              Details
            </h3>
            <dl className="space-y-1.5">
              {detailRows(detail.detail.sections, activity.contract.gstApplies).map((row) => (
                <div key={row.label} className="flex gap-3 text-body-sm">
                  <dt className="w-32 shrink-0 text-slate-500 dark:text-slate-400">
                    {row.label}
                  </dt>
                  <dd className="text-slate-900 dark:text-slate-100">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          {/* SERIES — only for a recurring activity. */}
          {detail.detail.seriesNeighbours && (
            <section>
              <h3 className="mb-2 text-overline uppercase text-slate-500 dark:text-slate-400">
                Series
              </h3>
              <SeriesProgressStrip
                occurrences={detail.detail.seriesNeighbours.occurrences.map(
                  (o) => ({ state: toDotState(o.occurrenceStatus) }),
                )}
                caption={`Series progress · ${
                  detail.detail.seriesNeighbours.occurrences.filter(
                    (o) => o.occurrenceStatus !== "future",
                  ).length
                } of ${detail.detail.seriesNeighbours.total} ${detail.detail.seriesNeighbours.cadenceLabel.toLowerCase()} occurrences`}
              />
            </section>
          )}

          {/* ABOUT. */}
          {detail.detail.description && (
            <section>
              <h3 className="mb-2 text-overline uppercase text-slate-500 dark:text-slate-400">
                About
              </h3>
              <p className="text-body-sm text-slate-700 dark:text-slate-300">
                {detail.detail.description}
              </p>
            </section>
          )}

          {/* LINKED CLAUSES. */}
          {detail.detail.linkedClauses.length > 0 && (
            <section>
              <h3 className="mb-2 text-overline uppercase text-slate-500 dark:text-slate-400">
                Linked clauses
              </h3>
              <ul className="space-y-1">
                {detail.detail.linkedClauses.map((c) => (
                  <li
                    key={`${c.ref}-${c.title}`}
                    className="text-body-sm text-slate-700 dark:text-slate-300"
                  >
                    <span className="font-mono text-caption text-slate-500 dark:text-slate-400">
                      {c.ref}
                    </span>{" "}
                    {c.title}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ACTIVITY log. */}
          {detail.detail.activityLog.length > 0 && (
            <section>
              <h3 className="mb-2 text-overline uppercase text-slate-500 dark:text-slate-400">
                Activity
              </h3>
              <ul className="space-y-1.5">
                {detail.detail.activityLog.map((e, i) => (
                  <li
                    key={`${e.at}-${i}`}
                    className="flex gap-3 text-body-sm"
                  >
                    <span className="w-28 shrink-0 text-caption text-slate-400 dark:text-slate-500">
                      {formatActivityLogTimestamp(e.at)}
                    </span>
                    <span className="text-slate-700 dark:text-slate-300">
                      {e.summary}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Action bar — single primary CTA. Editing happens in the
              per-tab editor reached via this link; an "Edit details"
              affordance in the drawer would be a no-op stub. */}
          <div className="flex items-center justify-end border-t border-slate-200 pt-4 dark:border-slate-700">
            <a
              href={`/manage/contracts/${activity.contract.id}?tab=${KIND_TO_TAB_SLUG[activity.kind]}&item=${activity.sourceId}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-body-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              Open in contract
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      )}
    </SlideOverDrawer>
  );
}
